import { reactive } from 'vue';
import { has, set, get, map, unset } from 'lodash';

interface Violation {
  propertyPath: string;
  title: string;
}

interface ErrorObject {
  data?: {
    title?: string;
    detail?: string;
    violations?: Violation[];
  };
}

/**
 * Provides error handling functionality using Vue's Composition API.
 * @returns {Object} Methods and reactive state for handling form errors and validation.
 */
export function useErrorHandler() {
  // Reactive state for violations
  const violations = reactive<Record<string, string[]>>({});

  /**
   * Build violations from the error object.
   * @param {ErrorObject} err - Error object containing the violations.
   */
  function buildViolations(err: ErrorObject) {
    if (!has(err, 'data.violations')) {
      return;
    }

    const newViolations: Record<string, string> = {};

    map(err.data?.violations, (value) => {
      if (value.propertyPath && value.title) {
        set(newViolations, value.propertyPath, value.title);
      }
    });

    Object.assign(violations, newViolations);
  }

  /**
   * Build a violation at the custom propertyPath.
   * @param {ErrorObject} err - Error object.
   * @param {string} propertyPath - The property path to set the violation.
   */
  function buildViolation(err: ErrorObject, propertyPath: string) {
    let message = '';

    if (has(err, 'data.title')) {
      message += err.data?.title || '';
    }

    if (has(err, 'data.detail')) {
      message += ': ' + (err.data?.detail || '');
    }

    if (!has(violations, propertyPath)) {
      set(violations, propertyPath, []);
    }

    (violations[propertyPath] as string[]).push(message);
  }

  /**
   * Clear all violations.
   */
  function clearViolations() {
    Object.keys(violations).forEach((key) => {
      unset(violations, key);
    });
  }

  /**
   * Clear a violation at the given property path.
   * @param {string} propertyPath - The property path to clear the violation.
   */
  function clearViolation(propertyPath: string) {
    unset(violations, propertyPath);
  }

  /**
   * Check if there is a violation for the given propertyPath.
   * @param {string} propertyPath - The property path to check.
   * @returns {boolean} Whether a violation exists at the propertyPath.
   */
  function hasViolation(propertyPath: string): boolean {
    if (!propertyPath) {
      return false;
    }

    return has(violations, propertyPath);
  }

  /**
   * Get the violation at the property path.
   * @param {string} propertyPath - The property path to get the violation.
   * @returns {string|boolean} The violation message or false if not found.
   */
  function getViolation(propertyPath: string): string | false {
    if (!propertyPath) {
      return false;
    }

    const violation = get(violations, propertyPath);

    // If the violation is an array, join it into a single string
    if (Array.isArray(violation)) {
      return violation.join(', ');
    }

    return violation || false;
  }

  /**
   * Get multiple violations at the given property paths.
   * @param {string[]} propertyPaths - The property paths to get the violations.
   * @returns {Object|Array} An object of violations or an empty array if none.
   */
  function getViolations(propertyPaths: string[]): Record<string, string> | [] {
    if (!propertyPaths) {
      return [];
    }

    const result: Record<string, string> = {};

    map(propertyPaths, (value) => {
      set(result, value, get(violations, value));
    });

    return result;
  }

  return {
    violations,
    buildViolations,
    buildViolation,
    clearViolations,
    clearViolation,
    hasViolation,
    getViolation,
    getViolations,
  };
}
